/**
 * 新增／編輯商品共用表單：前端檢核與 payload 組裝
 */

import { PRICE_FIELDS } from '@Local/Product/priceFields';

const NON_NEGATIVE_INTEGER_MESSAGE = '請輸入 0 以上的整數';

/** 是否為 0 以上整數字串（不含小數、負號） */
export const isNonNegativeIntegerText = (value) => /^\d+$/.test(String(value ?? '').trim());

/** 找出某層節點清單中指定 categoryID 的節點 */
export const findCategoryNodeById = (nodes, categoryID) => (nodes || []).find((node) => String(node.categoryID) === String(categoryID));

/** 在分類樹中找出從第一層到指定 categoryID 的 ID 路徑，找不到回傳 [] */
export const findCategoryPath = (nodes, categoryID, parents = []) => {
    for (const node of nodes || []) {
        const path = [...parents, node.categoryID];
        if (String(node.categoryID) === String(categoryID)) return path;
        const found = findCategoryPath(node.children, categoryID, path);
        if (found.length > 0) return found;
    }
    return [];
};

/**
 * 依已選路徑（categoryID 陣列）算出有效的最末層分類 ID
 * 只有選到「無子節點」的節點才是有效值；若最後選中的節點仍有子節點（代表尚未選到最末層），回傳 null
 */
export const getEffectiveCategoryID = (categoryOptions, selectedPath = []) => {
    let currentNodes = categoryOptions;
    for (let level = 0; level < selectedPath.length; level += 1) {
        const node = findCategoryNodeById(currentNodes, selectedPath[level]);
        if (!node) return null;
        if (!node.children || node.children.length === 0) return node.categoryID;
        currentNodes = node.children;
    }
    return null;
};

let clientKeySeq = 0;

/** 前端專用的卡片 key，不會送給後端 */
export const createClientKey = () => {
    clientKeySeq += 1;
    return clientKeySeq;
};

/** 建立一張空白規格卡片 */
export const createEmptyVariant = () => ({
    clientKey: createClientKey(),
    isExisting: false,
    variantName: '',
    sku: '',
    ...Object.fromEntries(PRICE_FIELDS.map(({ field }) => [field, ''])),
    unitAmount: '',
});

/**
 * 規格名稱是否必填：只有一個規格時非必填，兩個以上才必填（用來區分各規格）
 * @param {Array<object>} variants 規格卡片清單
 * @returns {boolean}
 */
export const isVariantNameRequired = (variants) => variants.length > 1;

/**
 * 驗證單張規格卡片，回傳欄位錯誤物件（無錯誤的欄位不列入）
 * @param {object} variant 卡片資料
 * @param {object} [options]
 * @param {boolean} [options.isVariantNameRequired=true] 規格名稱是否必填
 */
export const validateVariantCard = (variant, { isVariantNameRequired: variantNameRequired = true } = {}) => {
    const errors = {};
    if (variantNameRequired && !String(variant.variantName ?? '').trim()) errors.variantName = '請輸入規格名稱';
    if (!String(variant.sku ?? '').trim()) errors.sku = '請輸入 SKU';

    PRICE_FIELDS.forEach(({ field, required }) => {
        const text = String(variant[field] ?? '').trim();
        if (!required && text === '') return;
        if (!isNonNegativeIntegerText(text)) errors[field] = NON_NEGATIVE_INTEGER_MESSAGE;
    });

    // 單位個數必填，需為大於 0 的整數
    if (!(isNonNegativeIntegerText(variant.unitAmount) && Number(variant.unitAmount) > 0)) errors.unitAmount = '請輸入大於 0 的整數';

    return errors;
};

/**
 * 檢核整張商品表單，各欄位無錯誤時為空字串（variants 為各卡片的錯誤物件）
 * @returns {{productName: string, category: string, variantCount: string, defaultVariant: string, variants: Array<object>, hasError: boolean}}
 */
export const validateProductForm = ({ productName, categoryOptions, categoryPath, variants, defaultVariantIndex }) => {
    const variantOptions = { isVariantNameRequired: isVariantNameRequired(variants) };
    const errors = {
        productName: productName ? '' : '請輸入商品名稱',
        category: getEffectiveCategoryID(categoryOptions, categoryPath) ? '' : '請選擇商品分類',
        variantCount: variants.length > 0 ? '' : '請至少新增一個規格',
        defaultVariant: variants.length > 0 && defaultVariantIndex === null ? '請設定主規格' : '',
        variants: variants.map((variant) => validateVariantCard(variant, variantOptions)),
    };
    const hasError =
        Boolean(errors.productName || errors.category || errors.variantCount || errors.defaultVariant) ||
        errors.variants.some((fieldErrors) => Object.keys(fieldErrors).length > 0);
    return { ...errors, hasError };
};

/**
 * 組成後端新增／編輯商品 payload
 * @param {object} params
 * @param {string} params.productName
 * @param {number|null} params.categoryID 最末層分類 ID
 * @param {Array<object>} params.variants 規格卡片（依送出順序）
 * @param {number|null} params.defaultVariantIndex 主規格卡片索引
 * @returns {object} { mainSKU, productName, categoryID, variants: [{ variantName, sku, 五個價格欄位, unitAmount }] }
 */
export const buildProductPayload = ({ productName, categoryID, variants, defaultVariantIndex }) => ({
    mainSKU: String(variants[defaultVariantIndex]?.sku ?? '').trim(),
    productName: String(productName ?? '').trim(),
    categoryID,
    variants: variants.map((variant) => ({
        variantName: String(variant.variantName ?? '').trim(),
        sku: String(variant.sku ?? '').trim(),
        ...Object.fromEntries(PRICE_FIELDS.map(({ field }) => [field, Number(variant[field]) || 0])),
        unitAmount: Number(variant.unitAmount) || 0,
    })),
});
